import { Box, Checkbox, Stack, TextField } from '@atgfr8/atg-ui-tailwind';
import {
  CachedIcon,
  CheckIcon,
  ClockFastIcon,
  ClockIcon,
  ShieldCheckIcon,
} from '@atgfr8/icons';
import { BasicTable, Button } from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { v4ServeDocumentUrl } from '@constants/urls';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { getFalveyPricingQuoteData } from '@store/ltl/services';
import { ltlQuoteState } from '@store/ltl/store';
import { FalveyInsuranceQuoteInLoad, LTLLoadItem } from '@store/ltl/types';
import { LtlQuoteLoadItemViewModel } from '@store/orders';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount } from '@utils/utils';
import { isEmpty } from 'lodash';
import { Accessor, createSignal, onMount } from 'solid-js';
import * as yup from 'yup';

interface InsuranceUpdateProps {
  items: LTLLoadItem[] | LtlQuoteLoadItemViewModel[];
  onConfirmation: (
    val?: FalveyInsuranceQuoteInLoad,
    type?: string,
    declaredValue?: number,
  ) => void;
  onCancel: () => void;
  declaredValue?: number;
  updateDeclaredValue: (val: number) => void;
  customerCost?: number;
  carrierCost?: number;
  falveyInsuranceQuote?: FalveyInsuranceQuoteInLoad | undefined;
  ltlQuoteId?: string;
  quoteParametersId?: number;
  orderId?: number;
  isStoreQuoteLoading?: Accessor<boolean>;
}

const columns = [
  { name: 'Item Description', key: 'description' },
  { name: '# of Units', key: 'quantity' },
  { name: 'Weight', key: 'weight' },
  { name: 'Class', key: 'class' },
];

export const InsuranceUpdateAndConfirmationForm = (
  props: InsuranceUpdateProps,
) => {
  const [isConfirmed, setIsConfirmed] = createSignal(false);
  const [declaredVal, setDeclaredVal] = createSignal(props.declaredValue);
  const [currentDeclaredVal, setCurrentDeclaredVal] = createSignal(
    props.declaredValue,
  );
  const [falveyInsurance, setFalveyInsurance] =
    createSignal<FalveyInsuranceQuoteInLoad>();
  const [isLoadingBtn, setIsLoadingBtn] = createSignal<string>('');
  const [updateBtnClicked, setUpdateBtnClicked] = createSignal(false);
  const [declaredValError, setDeclaredValError] = createSignal<
    Record<string, string[]>
  >({});
  const showCarrierLiabilityInfo = isFeatureFlagEnabled(
    FeatureFlags.LtlLiabilityEducation,
  )();
  const updateBtnDisabled = () => {
    return (
      !isEmpty(declaredValError()) ||
      isLoadingBtn() !== '' ||
      !(currentDeclaredVal() !== declaredVal())
    );
  };
  const confirmedBtnDisabled = () => {
    return (
      (!isEmpty(declaredValError()) && !isConfirmed()) ||
      (isEmpty(falveyInsurance()) && !isConfirmed()) ||
      (props.isStoreQuoteLoading &&
        props.isStoreQuoteLoading() &&
        !isConfirmed()) ||
      (updateBtnDisabled() && !isConfirmed()) ||
      !updateBtnClicked()
    );
  };
  const fetchFalveyPricingQuote = async () => {
    setIsLoadingBtn('update');
    try {
      const resp = (await getFalveyPricingQuoteData({
        shipmentTypeID: '139015',
        declaredValue: declaredVal() as number,
        currency: 'USD',
        shippingMerchandiseDescription: '',
        ltlQuoteId: !isEmpty(props.ltlQuoteId) ? props.ltlQuoteId : undefined,
        quoteParametersId: props.quoteParametersId,
        ...(props.orderId ? { orderId: props.orderId } : {}),
      })) as { insurancePremium: number; serviceFee: number };
      setFalveyInsurance({
        quotePremium: resp.insurancePremium,
        quoteServiceFee: resp.serviceFee,
        shippingSumInsured: declaredVal() as number,
      });
    } finally {
      setIsLoadingBtn('');
    }
  };

  const handleUpdateButtonClick = async () => {
    await fetchFalveyPricingQuote();
    setUpdateBtnClicked(true);
    setCurrentDeclaredVal(declaredVal());
  };

  const getFalveyInsuraceRates = (type: 'insurance' | 'both') => {
    if (Boolean(falveyInsurance())) {
      const { quotePremium, quoteServiceFee } =
        falveyInsurance() as FalveyInsuranceQuoteInLoad;
      switch (type) {
        case 'insurance':
          return quotePremium;
        case 'both':
          return quotePremium + quoteServiceFee;
        default:
          return 0;
      }
    }
    return 0;
  };

  onMount(() => {
    if (props.falveyInsuranceQuote) {
      setFalveyInsurance(props.falveyInsuranceQuote);
    }
    if (Boolean(props.declaredValue)) {
      setUpdateBtnClicked(true);
    }
  });

  const Schema = yup.object().shape({
    declaredValue: yup
      .number()
      .max(999999, 'Must be less than 999999')
      .min(1, 'Must be greater than 0')
      .required('Declared value is required'),
  });

  const validateDeclaredValue = (vl: number) => {
    try {
      Schema.validateSync({ declaredValue: vl }, { abortEarly: false });
      setDeclaredValError({});
      return true;
    } catch (validationError: unknown) {
      const errors: Record<string, string[]> = {};
      if (validationError instanceof yup.ValidationError) {
        validationError.inner.forEach((error: yup.ValidationError) => {
          if (error.path !== undefined) {
            errors[error.path] = error.errors;
          }
        });
        setDeclaredValError(errors);

        return false;
      }
    }
  };

  return (
    <Box>
      {showCarrierLiabilityInfo && (
        <Box class="mb-2 mt-2">
          <Box class="text-lg leading-6 font-semibold text-[#292929]">
            About Carrier Liability
          </Box>
          <Box class="text-sm leading-5 text-[#4a4a4a] mt-1">
            The amounts shown reflect the maximum liability payout based on the
            shipment&apos;s weight and freight class. Please refer to the
            carrier&apos;s General Rules Tariff for complete terms, conditions,
            and coverage limitations, as certain commodities may not qualify for
            the maximum liability coverage.
          </Box>
          <Box class="mt-2 border border-[#0B76B8] bg-[#F0F8FF] p-2">
            <Box class="text-xl leading-7 font-semibold text-[#292929] mb-1.5">
              Why consider additional coverage?
            </Box>
            <Stack spacing={0.5}>
              <Box class="flex items-start gap-1.5">
                <Box class="mt-0.5 text-[#0B76B8]">
                  <ShieldCheckIcon size={20} color="#0B76B8" />
                </Box>
                <Box>
                  <Box class="text-sm leading-5 font-semibold text-[#1f2937]">
                    Full Value Coverage
                  </Box>
                  <Box class="text-xs leading-4 text-[#2b2b2b]">
                    Protect your shipment for its actual value.
                  </Box>
                </Box>
              </Box>
              <Box class="flex items-start gap-1.5">
                <Box class="mt-0.5 text-[#0B76B8]">
                  <ClockFastIcon size={20} />
                </Box>
                <Box>
                  <Box class="text-sm leading-5 font-semibold text-[#1f2937]">
                    Fast Claims Processing
                  </Box>
                  <Box class="text-xs leading-4 text-[#2b2b2b]">
                    Quick, hassle-free claims with Falvey.
                  </Box>
                </Box>
              </Box>
              <Box class="flex items-start gap-1.5">
                <Box class="mt-0.5 text-[#0B76B8]">
                  <ClockIcon size={20} />
                </Box>
                <Box>
                  <Box class="text-sm leading-5 font-semibold text-[#1f2937]">
                    All-Risk Protection
                  </Box>
                  <Box class="text-xs leading-4 text-[#2b2b2b]">
                    Covers loss or damage from most risks in transit.
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      )}

      <Box class="flex mb-2 items-center">
        <span class="text-xl font-semibold">Add Full Value Insurance</span>
        <span class="text-sm text-[#666666] ml-auto">
          Add a declared value to see your updated rates.
        </span>
      </Box>
      <Box class="flex gap-3 mb-2 items-center">
        <TextField
          label="Declared Value"
          placeholder="Input Cargo Value"
          value={Boolean(declaredVal()) ? String(declaredVal()) : ''}
          type="number"
          onChange={(val: unknown) => {
            setUpdateBtnClicked(false);
            setDeclaredVal(Number(val as number));
            validateDeclaredValue(declaredVal() as number);
          }}
          class="w-75! text-[16px]!"
          size="sm"
          error={
            !isEmpty(declaredValError())
              ? declaredValError()['declaredValue']
              : ''
          }
          dataTestId="declared-value-text-input"
        />
        <Button
          onClick={handleUpdateButtonClick}
          size="sm"
          label="Update"
          startIcon={isLoadingBtn() === 'update' ? undefined : <CachedIcon />}
          sx={{
            background: 'white',
            color: '#026EA1',
            border: '1px solid #026EA1',
          }}
          class="[&:hover]:bg-[#026EA1]! [&:hover]:text-white!"
          isLoading={isLoadingBtn() === 'update'}
          disabled={updateBtnDisabled()}
          dataTestId={formatDataTestId('Update', 'button')}
        />
      </Box>
      <Box class="font-semibold">Rates with Insurance</Box>
      <Box class="flex my-2">
        <Box class="flex">
          <Stack>
            <span class="font-semibold">Agent</span>
            <span class="text-[#666666] text-sm ml-4">Carrier Rate</span>
            <span class="text-[#666666] text-sm ml-4">Insurance Rate</span>
          </Stack>
          <Stack class="ml-auto!">
            <span class="text-[#26856f]">
              {formatAmount(
                (props.carrierCost ?? 0) + getFalveyInsuraceRates('insurance'),
              )}
            </span>
            <span class="text-sm ml-auto text-[#666666]">
              {formatAmount(props.carrierCost ?? 0)}
            </span>
            <span class="text-sm ml-auto text-[#666666]">
              {getFalveyInsuraceRates('insurance') !== 0
                ? formatAmount(getFalveyInsuraceRates('insurance'))
                : '--'}
            </span>
          </Stack>
        </Box>
        <Box class="flex ml-auto">
          <Stack>
            <span class="font-semibold">Customer</span>
            <span class="text-[#666666] text-sm ml-4">Insurance & Fees</span>
          </Stack>
          <Stack class="ml-auto!">
            <span class="text-[#26856f]">
              {formatAmount(
                (props.customerCost ?? 0) + getFalveyInsuraceRates('both'),
              )}
            </span>
            <span class="text-sm ml-auto text-[#666666]">
              {getFalveyInsuraceRates('both') !== 0
                ? formatAmount(getFalveyInsuraceRates('both'))
                : '--'}
            </span>
          </Stack>
        </Box>
      </Box>
      <Box class="flex items-center">
        <Checkbox
          size="xs"
          color="info"
          checked={isConfirmed()}
          label={''}
          onChange={(val) => setIsConfirmed(val)}
          class={'termsAndConditionsCheckbox'}
          dataTestId={formatDataTestId('Confirm', 'checkboxInput')}
        />
        <Box class="flex ml-2">
          I agree to the
          <Box class="ml-1">
            <a
              target={'_blank'}
              class="text-[#026EA1] underline"
              href={`${v4ServeDocumentUrl.doc(
                'Global%20Docs/0/072224_FalveyPolicy.pdf',
              )}`}
            >
              Terms and Conditions.
            </a>
            <span class="text-[#af0020]">*</span>
          </Box>
        </Box>
      </Box>
      <BasicTable<LTLLoadItem | LtlQuoteLoadItemViewModel>
        columns={columns}
        rows={props.items}
        cellClasses="border-b border-gray-300"
      />
      <Stack spacing={0.5} class="mt-4">
        <Box class="text-base leading-6 font-semibold">
          Deductibles (Based on Declared Value)
        </Box>
        <Box class="text-xs leading-5 whitespace-nowrap font-normal">
          $0 USD to $10,000 USD....................................$0
        </Box>
        <Box class="text-xs leading-5 whitespace-nowrap font-normal">
          $10,000.01 USD to $25,000 USD......................$500
        </Box>
        <Box class="text-xs leading-5 whitespace-nowrap font-normal">
          $25,000.01 USD to $100,000 USD....................$1,000
        </Box>
        <Box class="text-xs leading-5 whitespace-nowrap font-normal">
          Greater than $100,000.01.................................2% of
          Declared Value
        </Box>
      </Stack>
      <Box class="flex justify-end mt-4 gap-2">
        <Button
          label="Confirm"
          onClick={async () => {
            setIsLoadingBtn('confirm');
            await props.onConfirmation(
              falveyInsurance(),
              Boolean(props.declaredValue) ? 'update' : 'insert',
              declaredVal() as number,
            );
            props.updateDeclaredValue(declaredVal() as number);
            setIsLoadingBtn('');
          }}
          disabled={confirmedBtnDisabled()}
          isLoading={
            (props.isStoreQuoteLoading && props.isStoreQuoteLoading()) ||
            ltlQuoteState.isLoading
          }
          startIcon={<CheckIcon size={20} />}
          sx={{
            background: confirmedBtnDisabled() ? '' : '#278670',
          }}
          dataTestId={formatDataTestId('Confirm', 'button')}
        />
        <Button
          variant="outlined"
          label="Cancel"
          onClick={props.onCancel}
          disabled={ltlQuoteState.isLoading || isLoadingBtn() !== ''}
          dataTestId={formatDataTestId('Cancel', 'button')}
        />
      </Box>
    </Box>
  );
};
